import { apiUrl } from "../api/endpoints";
import React, { useEffect, useState, useContext, useCallback, useRef } from "react";
import { AddOrganization } from "./AddOrganization";
import axios from "axios";
import { useAuth } from "../hooks/AuthProvider";
import { STATIC_ICONS } from "../Constants";
import DeleteIcon from "@mui/icons-material/Delete";
import TranferIcon from "@mui/icons-material/SyncAltOutlined";
import { useNavigate } from "react-router-dom";
import "../Components/modal.css";
import "./edit.css";
import { deleteWithIcon, deleteMultiWithIcon } from "../lib/actions";
import DataGridTable from "../Components/DataGridTable";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { OrganizationIcon } from "../Icons";

import { LanguageContext } from "../context/LanguageContext";
import orgTranslations from "../languages/organization.json";
import { getErrorMessage } from "../utils/errorUtils";
import AppDialog from "../Components/AppDialog"; 
import TransferOrganizationModal from "../Components/TransferOrganizationModal";
import { normalizeOrganizationListRow } from "../utils/listRowIds";
import Spinner from "../Components/Spinner";

export const Organizations = () => {
  const { permissions, org } = useAuth();
  const canView = permissions["view_organizations"];
  const canCreate = permissions["create_organizations"];
  const canDelete = permissions["delete_organizations"];
  const canTransfer = permissions["transfer_organizations"];

  const [rows, setrows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([{ field: "name", sort: "asc" }]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [transfer, setTransfer] = useState({ open: false, id: 0 });
  const navigate = useNavigate();
  const pagingoptions = [10, 20, 50, 100];

  const { lang } = useContext(LanguageContext);
  const t = orgTranslations[lang];
  const isRtl = ["ar", "ur"].includes(lang);

  const resolverRef = useRef(null);
  const [dialog, setDialog] = useState({
	open: false,
	type: "alert", // "alert" | "confirm"
	variant: "info", // "info" | "danger" | "error"
	icon: "alert", // "alert" | "delete"
	title: "",
	message: "",
	confirmText: "Ok",
	cancelText: "Cancel",
	showCancel: false,
  });

  const closeDialog = useCallback((result = false) => {
	setDialog((d) => ({ ...d, open: false }));
	if (resolverRef.current) {
	  resolverRef.current(result);
	  resolverRef.current = null;
	}
  }, []);

  const alert = useCallback(
	(opts) =>
	  new Promise((resolve) => {
		resolverRef.current = resolve;

		setDialog({
		  open: true,
		  type: opts?.type || "alert",
		  variant: opts?.variant || "info",
		  icon: opts?.icon || "alert",
		  title: opts?.title || "",
		  message: opts?.message || "",
		  confirmText: opts?.confirmText || "Ok",
		  cancelText: opts?.cancelText || "Cancel",
		  showCancel: Boolean(opts?.showCancel),
		});
	  }),
	[]
  );

  const showAlert = useCallback(
	async (type, message) => {
	  const variant =
		type === "error" ? "error" : type === "warning" ? "info" : "info";

	  await alert({
		type: "alert",
		variant,
		icon: "alert",
		title:
		  type === "error"
			? t?.alert?.errorTitle || "Error"
			: type === "warning"
			? t?.alert?.warningTitle || "Warning"
			: t?.alert?.infoTitle || "Info",
		message: message || "",
		confirmText: "Ok",
	  });
	},
	[alert, t?.alert?.errorTitle, t?.alert?.warningTitle, t?.alert?.infoTitle]
  );

  const baseColumns = [
	{
	  field: "icon",
	  headerName: "",
	  width: 60,
	  align: "center",
	  renderCell: (params) =>
		params.row.icon ? (
		  <img
			src={STATIC_ICONS + params.row.icon}
			alt=""
			style={{
			  width: "32px",
			  height: "32px",
			  marginTop: "8px",
			  alignItems: "center",
			}}
		  />
		) : (
		  <OrganizationIcon color={"var(--accent)"} />
		),
	},
	{ field: "name", headerName: t.column.name, width: 200, align: "left", headerAlign: "left" },
	{
	  field: "device_count",
	  headerName: t.column.deviceCount,
	  width: 200,
	  type: "number",
	  align: "center",
	  headerAlign: "center",
	},
	{
	  field: "total_device_count",
	  headerName: t.column.totalDeviceCount,
	  width: 200,
	  type: "number",
	  align: "center",
	  headerAlign: "center",
	},
	{
	  field: "user_count",
	  headerName: t.column.userCount,
	  width: 200,
	  type: "number",
	  align: "center",
	  headerAlign: "center",
	},
	{
	  field: "sub_organizations",
	  headerName: t.column.subOrganizations,
	  width: 200,
	  align: "center",
	  headerAlign: "center",
	},
  ];

  const columns = canDelete || canTransfer
	? [
		{
		  field: "actions",
		  headerName: t.column.actions,
		  type: "actions",
		  width: 100,
		  getActions: (params) => [
			canTransfer && Number(params.row.parent) > 0 ? (
			  <GridActionsCellItem
				icon={<TranferIcon />}
				showInMenu
				label={t?.organizationDetails?.transferTitle || "Transfer Organization"}
				onClick={() => setTransfer({ open: true, id: params.row.id })}
			  />
			) : null,
			canDelete ? (
			  <GridActionsCellItem
				icon={<DeleteIcon style={{ color: "red" }} />}
				showInMenu
				label={t.delete}
				onClick={() =>
				  deleteWithIcon(params.row.id, params.row.icon, "organization", fetchOrganizations, alert)
				}
			  />
			) : null,
		  ].filter(Boolean),
		},
		...baseColumns,
	  ]
	: baseColumns;

  const fetchOrganizations = useCallback(async () => {
	if (!canView) {
	  setrows([]);
	  setRowCount(0);
	  setLoading(false);
	  return;
	}

	if (!org || !org.id) {
	  setrows([]);
	  setRowCount(0);
	  setLoading(false);
	  return;
	}

	if (!Number.isInteger(org.id) || org.id < 1) {
	  await showAlert("error", "Invalid parent Organization ID.");
	  setLoading(false);
	  return;
	}

	setLoading(true);
	try {
	  const activeSort = sortModel && sortModel.length > 0 ? sortModel[0] : { field: "name", sort: "asc" };
	  const result = await axios.get(apiUrl(`organization/filtered/${org.id}`), {
		params: {
		  page,
		  pageSize,
		  sortField: activeSort.field || "name",
		  sortOrder: activeSort.sort || "asc",
		  filterModel: JSON.stringify(filterModel || { items: [] }),
		},
	  });

	  if (result.data && result.data.status === "OK") {
		setrows(
		  Array.isArray(result.data.data)
			? result.data.data.map(normalizeOrganizationListRow)
			: []
		);
		setRowCount(result.data?.pagination?.total ?? result.data?.data?.length ?? 0);
	  } else {
		await showAlert("error", "Failed to retrieve sub-organizations.");
		setrows([]);
		setRowCount(0);
	  }
	} catch (err) {
	  console.error("API Error:", err);
	  const errorMessage = getErrorMessage(err);
	  await showAlert("error", errorMessage || "An unexpected error occurred while fetching data.");
	  setrows([]);
	  setRowCount(0);
	} finally {
	  setLoading(false);
	}
  }, [canView, filterModel, org, page, pageSize, showAlert, sortModel]);

  useEffect(() => {
	fetchOrganizations();
  }, [fetchOrganizations]);

  const handleRowClick = (params) => {
	navigate("/administration/organizations/details/" + params.row.id);
  };

  const handleMultiDelete = async (ids) => {
	if (!canDelete) {
	  await showAlert("error", "You do not have permission to delete organizations.");
	  return;
	}

	const icons = [];
	ids.forEach((id) => {
	  const selectedRow = rows.find((row) => row.id === id);
	  if (selectedRow) icons.push(selectedRow.icon);
	});

	await deleteMultiWithIcon(ids, icons, "organization", fetchOrganizations, alert);
	setSelectedRows([]);
  };

  const [selectedRows, setSelectedRows] = React.useState([]);

  const selectActions = canDelete
	? [
		{
		  title: t.delete,
		  icon: <DeleteIcon style={{ color: "rgb(236, 69, 78)", marginInlineEnd: "20px" }} />,
		  action: (ids) => handleMultiDelete(ids),
		},
	  ]
	: [];

  const colors = {
	headerBackground: "var(--header-background)",
	headerColor: "var(--header-color)",
	headerHover: "var(--header-hover)",
	cellBackground: "var(--cell-background)",
	cellColor: "var(--cell-color)",
	cellHover: "var(--cell-hover)",
	orderColor: "var(--order-color)",
	orderSelectedColor: "rgb(49, 199, 149)",
	checkboxSelection: "var(--accent)",
	btnBackground: "rgb(219, 245, 236)",
	btnColor: "var(--accent)",
	modalBorder: "var(--modal-border)",
	red: "rgb(236, 69, 78)",
  };

  if (!canView) {
	return <div style={{ padding: "20px" }}>{t.noPermission}</div>;
  }

  return (
	<div className="configuration devices-page" style={{ display: "flex", flexDirection: "column", height: "100%" }} dir={isRtl ? "rtl" : "ltr"}>
	  <AppDialog
		open={dialog.open}
		type={dialog.type}
		variant={dialog.variant}
		icon={dialog.icon}
		title={dialog.title}
		message={dialog.message}
		confirmText={dialog.confirmText}
		cancelText={dialog.cancelText}
		showCancel={dialog.showCancel}
		onConfirm={() => closeDialog(true)}
		onCancel={() => closeDialog(false)}
		onClose={() => closeDialog(false)}
	  />

	  {openAddModal && (
		<AddOrganization
		  bg={colors.cellBackground}
		  fg={colors.cellColor}
		  bd={colors.modalBorder}
		  ft={colors.headerBackground}
		  btnPrimary={colors.btnBackground}
		  btnSecondary={colors.btnColor}
		  textColor={colors.headerColor}
		  setOpen={setOpenAddModal}
		  refresh={fetchOrganizations}
		/>
	  )}

	  {transfer.open && (
		<TransferOrganizationModal
		  state={transfer}
		  setState={setTransfer}
		  refresh={fetchOrganizations}
		/>
	  )}

	  <div className="device-main">
		<div className="devices-header">
		  <div className="devices-header-left">
			<h2 className="devices-title">{t.organizationsPageTitle}</h2>
		  </div>

		  {canCreate ? (
			<button className="devices-primary-btn" onClick={() => setOpenAddModal(true)}>
			  <span>{t.newOrganization}</span>
			</button>
		  ) : null}
		</div>

		{loading && rows.length === 0 && rowCount === 0 && !(filterModel?.items || []).length ? (
		  <Spinner />
		) : rows.length > 0 || rowCount > 0 || (filterModel?.items || []).length > 0 ? (
		  <div className="table-wrapper" style={{ flex: 1, minHeight: 0 }}>
			<DataGridTable
			  columns={columns}
			  rows={rows}
			  handleRowClick={handleRowClick}
			  idcolumn={"id"}
			  pagingoptions={pagingoptions}
			  checkboxSelection={canDelete}
			  rowSelectionModel={selectedRows}
			  setRowSelectionModel={setSelectedRows}
			  selectActions={selectActions}
			  showToolbar={true}
			  tableKey="organizations"
			  loading={loading}
			  pagination={{
				serverSide: true,
				page,
				pageSize,
				count: rowCount,
				sortModel,
				onPageChange: (newPage) => setPage(newPage),
				onPageSizeChange: (newSize) => {
				  setPageSize(newSize);
				  setPage(1);
				},
				onSortModelChange: (newModel) => {
				  setSortModel(newModel && newModel.length ? newModel : []);
				  setPage(1);
				},
				onFilterModelChange: (newModel) => {
				  setFilterModel(newModel || { items: [] });
				  setPage(1);
				},
			  }}
			/>
		  </div>
		) : (
		  <div style={{ width: "100%", flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
			{t.noData}
		  </div>
		)}
	  </div>
	</div>
  );
};
